/**
 * Signature Fonts Configuration
 *
 * This file defines the available fonts, colors, and sizes for agent signatures.
 * Fonts must match the backend allowed list and be loaded via Google Fonts.
 */

import type { SignatureFont, SignatureColor, SignatureSizeOption } from 'types/signature';

/**
 * Available signature fonts (loaded from Google Fonts in index.html)
 * These fonts must match the ALLOWED_FONTS list in the backend
 */
export const SIGNATURE_FONTS: SignatureFont[] = [
    {
        name: 'Allura',
        value: 'Allura',
        googleFontName: 'Allura',
    },
    {
        name: 'Dancing Script',
        value: 'Dancing Script',
        googleFontName: 'Dancing+Script',
    },
    {
        name: 'Great Vibes',
        value: 'Great Vibes',
        googleFontName: 'Great+Vibes',
    },
    {
        name: 'Pacifico',
        value: 'Pacifico',
        googleFontName: 'Pacifico',
    },
    {
        name: 'Satisfy',
        value: 'Satisfy',
        googleFontName: 'Satisfy',
    },
    {
        name: 'Cookie',
        value: 'Cookie',
        googleFontName: 'Cookie',
    },
    {
        name: 'Kaushan Script',
        value: 'Kaushan Script',
        googleFontName: 'Kaushan+Script',
    },
    {
        name: 'Shadows Into Light',
        value: 'Shadows Into Light',
        googleFontName: 'Shadows+Into+Light',
    },
];

/**
 * Available signature colors
 * These colors must match the ALLOWED_COLORS list in the backend
 */
export const SIGNATURE_COLORS: SignatureColor[] = [
    {
        name: 'Black',
        value: '#000000',
    },
    {
        name: 'Dark Blue',
        value: '#1e40af',
    },
    {
        name: 'Navy Blue',
        value: '#1e3a8a',
    },
];

/**
 * Available signature sizes
 */
export const SIGNATURE_SIZES: SignatureSizeOption[] = [
    {
        name: 'Small',
        value: 'small',
        description: 'Best for inline text',
    },
    {
        name: 'Medium',
        value: 'medium',
        description: 'Standard signature size',
    },
    {
        name: 'Large',
        value: 'large',
        description: 'Prominent signature display',
    },
];

/**
 * Default signature configuration values
 */
export const SIGNATURE_DEFAULTS = {
    color: '#000000',
    size: 'medium' as const,
    font: 'Allura',
};

/**
 * Signature validation constraints
 */
export const SIGNATURE_CONSTRAINTS = {
    MAX_SIGNATURES_PER_USER: 10,
    MAX_IMAGE_SIZE_MB: 2,
    MAX_IMAGE_SIZE_BYTES: 2 * 1024 * 1024,
    ALLOWED_IMAGE_TYPES: ['image/png', 'image/jpeg'],
    ALLOWED_IMAGE_EXTENSIONS: ['.png', '.jpg', '.jpeg'],
    MAX_TYPED_TEXT_LENGTH: 128,
    MAX_SVG_DATA_LENGTH: 10000,
    MAX_DISPLAY_NAME_LENGTH: 128,
    MAX_TITLE_LENGTH: 128,
    MAX_SIGNATURE_NAME_LENGTH: 64,
};

/**
 * Helper function to get font family CSS string
 */
export const getFontFamilyCSS = (fontValue: string): string => {
    return `'${fontValue}', cursive`;
};

/**
 * Helper function to get font size in pixels based on size enum
 */
export const getFontSizePixels = (size: 'small' | 'medium' | 'large'): string => {
    const sizeMap = {
        small: '24px',
        medium: '32px',
        large: '48px',
    };
    return sizeMap[size] || sizeMap.medium;
};

/**
 * Helper function to get signature width based on size enum
 */
export const getSignatureWidth = (size: 'small' | 'medium' | 'large'): string => {
    const widthMap = {
        small: '150px',
        medium: '200px',
        large: '300px',
    };
    return widthMap[size] || widthMap.medium;
};

/**
 * Helper function to validate image file
 */
export const validateImageFile = (file: File): { valid: boolean; error?: string } => {
    // Check file type
    if (!SIGNATURE_CONSTRAINTS.ALLOWED_IMAGE_TYPES.includes(file.type)) {
        return {
            valid: false,
            error: 'Invalid file type. Only PNG and JPEG images are allowed.',
        };
    }

    // Check file size
    if (file.size > SIGNATURE_CONSTRAINTS.MAX_IMAGE_SIZE_BYTES) {
        return {
            valid: false,
            error: `File size must be less than ${SIGNATURE_CONSTRAINTS.MAX_IMAGE_SIZE_MB}MB.`,
        };
    }

    return { valid: true };
};

/**
 * Helper function to convert file to base64
 */
export const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            const result = reader.result as string;
            // Remove the data:image/...;base64, prefix
            const base64 = result.split(',')[1];
            resolve(base64);
        };
        reader.onerror = (error) => reject(error);
    });
};
